import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  AlliesProgressModalData,
  BillingData,
  CaseData,
  ProgressData,
  SaleData,
  totalCases,
} from '@core/interfaces/partners-ALI';
import { LocalStorageService } from '@core/services/local-storage.service';
import { PartnersALIService } from '@core/services/partners-ALI/partners-ALI.service';
import { UtilsService } from '@core/services/utils/utils.service';
import { Carousel } from 'primeng/carousel';
import { take } from 'rxjs';
import { MessageAlliesProgressError } from 'src/assets/data/errors-message';

@Component({
  selector: 'app-allies-progress-modal',
  templateUrl: './allies-progress-modal.component.html',
  styleUrls: ['./allies-progress-modal.component.scss'],
})
export class AlliesProgressModalComponent implements OnInit {
  @Input() currentYear;
  @Input() mockProgressData!: ProgressData;
  @Input() saleData: SaleData | null = null;

  @Output() scrollToBenefits = new EventEmitter<void>();

  alignmentClass = 'text-start-dynamic';
  readonly alignmentClassMobile = 'text-start-dynamic';

  activeCaseData: CaseData | null = null;

  billingData: BillingData = [];
  loadingSummary = false;
  errorSummary=false;

  silver: number | undefined;
  gold: number | undefined;
  diamond: number | undefined;

  readonly cards = [
    { id: 1, name: 'Card 1' },
    { id: 2, name: 'Card 2' },
    { id: 3, name: 'Card 3' },
  ];

  @ViewChild('carouselGeneralALI') carouselGeneralALI!: Carousel;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  readonly Math = Math;
  readonly messageAlliesProgressError = [MessageAlliesProgressError];
  constructor(
    private readonly dialog: MatDialog,
    public readonly dialogRef: MatDialogRef<AlliesProgressModalComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: AlliesProgressModalData,
    private readonly _partnersALIService: PartnersALIService,
    private readonly router: Router,
    public readonly _localStorageService: LocalStorageService, private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarDatosDesdeElModal();
    this.updateTextAlignment();
    this.configurarCasoActivo();
    this.cargarNivelesDeVenta();
    this.obtenerResumenDeFacturacion();
  }

  /**
   * Asigna los datos recibidos desde el modal al componente
   */
  private cargarDatosDesdeElModal(): void {
    this.currentYear = this.data.currentYear;
    this.mockProgressData = this.data.mockProgressData;
    this.alignmentClass = this.data.alignmentClass;
    this.saleData = this.data.saleData;
    this.activeCaseData = this.data.activeCaseData;
  }

  /**
   * Determina y asigna el caso activo del cliente con título adecuado
   */
  private configurarCasoActivo(): void {
    const caseCode = this.saleData?.actualState?.case;
    const description = this.saleData?.actualState?.description;

    const matchedCase = totalCases.find((c) => c.case === caseCode);

    this.activeCaseData = matchedCase
      ? { ...matchedCase, title: description || matchedCase.title }
      : null;
  }

  /**
   * Carga los niveles de venta (targets para plata, oro y diamante)
   */
  private cargarNivelesDeVenta(): void {
    const levels = this.saleData?.customerSaleLevels;

    if (!levels || levels.length < 4) return;

    this.silver = levels[1]?.targetAmount;
    this.gold = levels[2]?.targetAmount;
    this.diamond = levels[3]?.targetAmount;
  }

  /**
   * Obtiene el resumen de facturación del cliente con control de loading y errores
   */
  private obtenerResumenDeFacturacion(): void {
    const isAliSocios = this.router.url === '/main/ali-socios';

    const code = isAliSocios
      ? this._localStorageService.getItem('clientSelect')?.code
      : this._localStorageService.getItem('user')?.code;

    const sector = isAliSocios
      ? this._localStorageService.getItem('clientSelect')?.sector
      : this._localStorageService.getItem('sectorSelect')?.sector;

    if (!code || !sector) {
      this.errorSummary = true;
      return;
    }

    const body = { code, sector };

    this.loadingSummary = true;
    this.errorSummary = false;

    this._partnersALIService.getOfferSummaryByCode(body)
      .pipe(take(1))
      .subscribe({
        next: (res: BillingData) => {
          this.billingData = res;
          this.loadingSummary = false;
        },
        error: (err) => {
          this.errorSummary = true;
          this.loadingSummary = false;
        }
      });
  }

  // Calcula el total acumulado
  get totalAcumulado(): number {
    return this.billingData.reduce(
      (acc, trimestre) => acc + (trimestre.total || 0),
      0
    );
  }

  // Formatea total
  formatTotal(total: number): string {
    return total == 0 ? '0.00' : `${this.formatTotalNumber(total.toFixed(2))}`;
  }

  shouldShowTooltip(level: string): boolean {
    const currentPosition = this.saleData?.customerActualLevel?.position;

    if (currentPosition === undefined) {
      return false;
    }

    const levelPositions: { [key: string]: number } = {
      bronce: 1,
      silver: 2,
      gold: 3,
      diamond: 4,
    };

    const levelToCheck = levelPositions[level.toLowerCase()];
    return levelToCheck > currentPosition;
  }

  getRemainingPercentageToNextLevel(): number {
    const percentage = this.saleData?.customerActualLevel?.progressPercentage;

    if (percentage === undefined || percentage === null) {
      return 0;
    }

    return Math.max(0, 100 - Math.round(percentage));
  }

  getAvatarPosition(): number {
    return Math.min(this.mockProgressData.avatarPosition - 1.8, 97);
  }

  // Obtener el promedio mensual requerido hasta fin de año
  getMonthlyAverageRequired(): number {
    const current = this.mockProgressData.currentAmount;
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-11 (Enero = 0)
    const monthsRemaining = 12 - currentMonth; // Meses restantes del año

    let amountNeeded = 0;

    if (current < this.silver!) {
      // Bronce -> Plata
      amountNeeded = this.silver! - current;
    } else if (current < this.gold!) {
      // Plata -> Oro
      amountNeeded = this.gold! - current;
    } else if (current < this.diamond!) {
      // Oro -> Diamante
      amountNeeded = this.diamond! - current;
    } else {
      // Diamante: si supera el objetivo, mostrar negativo
      if (current > this.diamond!) {
        return -(current - this.diamond!);
      }
      return 0; // Solo si está exactamente en diamante
    }

    const monthlyAverage = amountNeeded / monthsRemaining;
    return Math.max(monthlyAverage, 0);
  }

  // Obtener la medalla del próximo nivel
  achievementCardMiddle(actualState: number): string {
    switch (actualState) {
      case 1:
        return 'achievement-card-large-1';
      case 2:
        return 'achievement-card-large-2';
      case 3:
        return 'achievement-card-large-3';
      case 4:
        return 'achievement-card-large-4';
      case 5:
        return 'achievement-card-large-5';
      case 6:
        return 'achievement-card-large-6';
      default:
        return '';
    }
  }

  // Obtener la medalla del próximo nivel
  getNextLevelMedal(current: string): string {
    switch (current) {
      case 'Bronce':
        return '/assets/image/partners-ali/medal-bronce.svg';
      case 'Plata':
        return '/assets/image/partners-ali/medal-plata.svg';
      case 'Oro':
        return '/assets/image/partners-ali/medal-oro.svg';
      case 'Diamante':
        return '/assets/image/partners-ali/medal-diamond.svg';
      default:
        return '/assets/image/partners-ali/medal-bronce.svg';
    }
  }

  // Obtener el faltante para un nivel específico
  getRemainingForLevel(level: string): number {
    const current = this.mockProgressData.currentAmount;

    switch (level) {
      case 'silver':
        return Math.max(this.silver! - current, 0);
      case 'gold':
        return Math.max(this.gold! - current, 0);
      case 'diamond':
        return Math.max(this.diamond! - current, 0);
      default:
        return 0;
    }
  }

  getCurrentLevelMobile(): number {
    const levelName = this.saleData?.customerActualLevel?.name?.toLowerCase();

    switch (levelName) {
      case 'bronce':
        return 1;
      case 'plata':
        return 2;
      case 'oro':
      case 'diamante':
        return 3;
      default:
        return 1;
    }
  }
 getLevelMessageStrict(levelName: string): string | null {
    const currentLevel = this.saleData?.actualLevelName;
    const currentCase = this.saleData?.actualState?.case;

    if (currentLevel !== levelName || currentCase == null) return null;

    if ([1, 2].includes(currentCase)) {
      return 'Asegura tu nivel';
    }

    if ([3, 4].includes(currentCase) && levelName !== 'Diamante') {
      return 'Aseguraste tu nivel';
    }

    return null;
  }

  @HostListener('window:resize', [])
  onWindowResize() {
    this.updateTextAlignment();
  }

  updateTextAlignment() {
    const threshold = 7;
    const avatarPosition = this.mockProgressData.avatarPosition;
    if (avatarPosition <= threshold) {
      this.alignmentClass = 'text-start-dynamic';
    } else if (avatarPosition >= 80) {
      this.alignmentClass = 'text-end-dynamic';
    } else {
      this.alignmentClass = 'text-center-dynamic';
    }
    this.cdr.detectChanges();
  }


  prevPage() {
    this.carouselGeneralALI.navBackward(new Event('click'));
  }

  nextPage() {
    this.carouselGeneralALI.navForward(new Event('click'));
  }

  goToPage(page: number) {
    this.carouselGeneralALI.page = page;
  }

  getTotalPages() {
    return this.cards;
  }

  getCardIndex(card: any): number {
    return this.cards.indexOf(card);
  }

 shouldShowAvatar(cardId: number): boolean {
    return this.getCurrentLevelCards().includes(cardId);
  }
  getCurrentLevelCards(): number[] {
    const amount = this.saleData?.invocedTotalAmount ?? 0;
    const levels = this.saleData?.customerSaleLevels ?? [];

    if (!levels.length) return [];

    const cards: number[] = [];

    // Definir los tramos según card
    const slideToLevels: Record<number, [number, number]> = {
      1: [1, 2], // Bronce → Plata
      2: [2, 3], // Plata → Oro
      3: [3, 4], // Oro → Diamante
    };

    for (const [cardId, [fromPos, toPos]] of Object.entries(slideToLevels)) {
      const fromLevel = levels.find(l => l.position === fromPos);
      const toLevel = levels.find(l => l.position === toPos);

      if (!fromLevel || !toLevel) continue;

      const fromTarget = fromLevel.targetAmount ?? 0;
      const toTarget = toLevel.targetAmount ?? 0;

      // Caso especial: si está exactamente en el límite, mostrar en ambos
      if (amount === toTarget) {
        cards.push(Number(cardId), Number(cardId) + 1);
        break;
      }

      // Caso normal: si está dentro del tramo → asignar ese card
      if (amount >= fromTarget && amount < toTarget) {
        cards.push(Number(cardId));
        break;
      }

      // Si superó todos → última card
      if (amount >= levels[levels.length - 1].targetAmount) {
        cards.push(3);
        break;
      }
    }

    return cards;
  }

  alignmentClassmobile(idCard) {
    const threshold = 15;
    const avatarPosition = this.getLocalProgress(idCard, true);

    if (avatarPosition <= threshold) {
      return 'text-start-dynamic';
    } else if (avatarPosition >= 72) {
      return 'text-end-dynamic';
    } else {
      return 'text-center-dynamic';
    }
  }
  getLocalProgress(slideId: number, isForAvatar: boolean): number {
    const amount = this.saleData?.invocedTotalAmount ?? 0;
    const levels = this.saleData?.customerSaleLevels ?? [];

    if (!levels.length) return 0;

    const isMobile = window.innerWidth <= 380 ? 85 : 90.5;
    const maxProgress = isForAvatar ? isMobile : 100;

    // Definir rangos según slide
    const slideToLevels: Record<number, [number, number]> = {
      1: [1, 2], // Bronce → Plata
      2: [2, 3], // Plata → Oro
      3: [3, 4], // Oro → Diamante
    };

    const [fromPos, toPos] = slideToLevels[slideId] || [];
    const fromLevel = levels.find(l => l.position === fromPos);
    const toLevel = levels.find(l => l.position === toPos);

    if (!fromLevel || !toLevel) return 0;

    const fromTarget = fromLevel.targetAmount ?? 0;
    const toTarget = toLevel.targetAmount ?? 0;

    // Caso 1: ya superó el rango → progreso completo
    if (amount >= toTarget) {
      return maxProgress;
    }

    // Caso 2: aún no llegó → progreso relativo
    if (amount > fromTarget) {
      const relative = ((amount - fromTarget) / (toTarget - fromTarget)) * maxProgress;
      return Math.min(Math.max(relative, 0), maxProgress);
    }

    // Caso 3: no alcanzó ni el inicio del tramo
    return 0;
  }

   getTooltipPositionClass(level: string): string {
    switch (level.toLowerCase()) {
      case 'bronze':
        return 'tooltip-left';
      case 'silver':
        return 'tooltip-center';
      case 'gold':
        return 'tooltip-center';
      case 'diamond':
        return 'tooltip-right';
      default:
        return 'tooltip-center';
    }
  }
  formatTotalNumber(value: any): string {
  if (value === '-') {
    return '-';
  }

  if (value === 0 || value === '0.00') {
    return '0.00';
  }

  if (typeof value === 'number') {
    return UtilsService.formatTotalNumber(value);
  }

  if (typeof value === 'string' && !Number.isNaN(Number(value))) {
    return UtilsService.formatTotalNumber(Number(value));
  }

  return '';
}

close(): void {
    this.dialogRef.close();
}
}
